/** @format */

import React, {useMemo, forwardRef, useState, useCallback, useRef} from 'react';

import {TableVirtualized} from 'components/Table';

import {EntityType, ENTITY_TYPE_HEADERS, ENTITY_TYPE_ICONS} from '../../constants';
import {SegmentTableContainer, SegmentHeader} from './styles';
import {createSegmentTableColumns} from '../../helpers/createSegmentTableColumns';
import {TableRow} from '../../types';
import {mapSegmentsToTableData} from './helpers/mapSegmentsToTableData';

interface SegmentTableProps {
  entityType: EntityType;
  segments: TableRow[];
  maxKpiFact: number;
  maxForecastKpi: number;
  maxForecastBudget: number;
  kpiName: string;
  kpiUnits: string;
  targetKpi: number;
  targetBudget: number;
  plannedBudget: number;
  onCampaignFilterClick?: (campaignId: string) => void;
  onOpenSubRows?: (isOpen: boolean, campaignId?: string) => void;
  onCampaignNameClick?: (campaignId: string) => void;
}

const ROW_HEIGHT = 61;
const HEADER_HEIGHT = 72;

const SegmentTable = forwardRef<HTMLDivElement, SegmentTableProps>(
  (
    {
      entityType,
      segments,
      maxKpiFact,
      maxForecastKpi,
      maxForecastBudget,
      kpiName,
      kpiUnits,
      targetKpi,
      targetBudget,
      plannedBudget,
      onCampaignFilterClick,
      onOpenSubRows,
      onCampaignNameClick,
    },
    ref,
  ) => {
    const headerTitle = ENTITY_TYPE_HEADERS[entityType] || 'Сегменты';
    const IconComponent = ENTITY_TYPE_ICONS[entityType];
    const baseTableHeight = HEADER_HEIGHT + segments.length * ROW_HEIGHT + 20;

    const [tableHeight, setTableHeight] = useState(baseTableHeight);

    const tableData = useMemo(() => mapSegmentsToTableData(segments), [segments]);

    const lastExpandedCampaignId = useRef<string | null>(null);

    const handleToggleDetails = useCallback((campaignId: string | null) => {
      lastExpandedCampaignId.current = campaignId;
    }, []);

    const handleOpenSubRows = useCallback(
      (isOpen: boolean) => {
        setTableHeight(isOpen ? baseTableHeight + 220 : baseTableHeight);
        onOpenSubRows?.(isOpen, lastExpandedCampaignId.current ?? undefined);
      },
      [baseTableHeight, onOpenSubRows],
    );

    const columns = useMemo(
      () =>
        createSegmentTableColumns({
          entityType,
          maxKpiFact,
          maxForecastKpi,
          maxForecastBudget,
          kpiName,
          kpiUnits,
          targetKpi,
          targetBudget,
          plannedBudget,
          onCampaignFilterClick,
          onCampaignNameClick,
          onToggleDetails: handleToggleDetails,
        }),
      [
        entityType,
        maxKpiFact,
        maxForecastKpi,
        maxForecastBudget,
        kpiName,
        kpiUnits,
        targetKpi,
        targetBudget,
        plannedBudget,
        onCampaignFilterClick,
        onCampaignNameClick,
        handleToggleDetails,
      ],
    );

    return (
      <SegmentTableContainer ref={ref} id={`segment-table-${entityType}`}>
        <SegmentHeader>
          {IconComponent && <IconComponent />}
          {headerTitle}
        </SegmentHeader>
        <TableVirtualized
          columns={columns}
          data={tableData}
          tableHeight={tableHeight}
          enableSorting
          onOpenSubRows={handleOpenSubRows}
        />
      </SegmentTableContainer>
    );
  },
);

SegmentTable.displayName = 'SegmentTable';

export default SegmentTable;
